import {
  forwardRef,
  useImperativeHandle,
  useRef,
  useState,
} from "react"
import { Loader2Icon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Textarea } from "@/components/ui/textarea"

export interface ChatInputHandle {
  focus: () => void
}

interface ChatInputProps {
  onSubmit?: (message: string) => void
  disabled?: boolean
  /** True while the chat request is in flight; shows a spinner on Send */
  loading?: boolean
  /** Shown when the submit button is disabled (e.g. "Select a jurisdiction first") */
  disabledTitle?: string
}

export const ChatInput = forwardRef<ChatInputHandle, ChatInputProps>(
  function ChatInput(
    { onSubmit, disabled = false, loading = false, disabledTitle },
    ref
  ) {
    const [value, setValue] = useState("")
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const formRef = useRef<HTMLFormElement>(null)

    useImperativeHandle(ref, () => ({
      focus: () => textareaRef.current?.focus(),
    }))

    function handleSubmit(e: React.FormEvent) {
      e.preventDefault()
      if (disabled) return
      const message = value.trim()
      if (!message) return
      onSubmit?.(message)
      setValue("")
    }

    function handleKeyDown(e: React.KeyboardEvent<HTMLTextAreaElement>) {
      if (e.key !== "Enter") return
      if (e.shiftKey) return
      if (e.nativeEvent.isComposing) return
      e.preventDefault()
      formRef.current?.requestSubmit()
    }

    const buttonTitle = loading
      ? "Sending message…"
      : disabled
        ? disabledTitle
        : undefined

    return (
      <form
        ref={formRef}
        onSubmit={handleSubmit}
        className="flex w-full items-end gap-2"
      >
        <Textarea
          ref={textareaRef}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Message..."
          className="min-h-[44px] max-h-[200px] resize-none"
          aria-label="Chat message"
          aria-busy={loading}
          rows={1}
          disabled={disabled}
        />
        <span className="shrink-0" title={buttonTitle}>
          <Button
            type="submit"
            size="default"
            disabled={disabled}
            className="min-w-[5.5rem]"
            aria-busy={loading}
            aria-label="Send"
          >
            {loading ? (
              <span className="inline-flex items-center justify-center gap-2">
                <Loader2Icon
                  className="size-4 shrink-0 animate-spin"
                  aria-hidden
                />
                <span className="sr-only">Sending</span>
              </span>
            ) : (
              "Send"
            )}
          </Button>
        </span>
      </form>
    )
  }
)
